import React, { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { blankStyleOverrides, stylingOverrideReturns } from "../../../../../live-event/utils";
import { LanguagesAbbr, Session, SessionTypesEnum, TranslateString } from "../../../../../../types/working-model";
import { TemplateClassNames } from "../../../../../../types/template-layouts";
import { EventsState } from "../../../../../../store/types";
import { TIconName } from "../../../../../general-ui/icon/icon.types";
import { Tag } from "../../../../../general-ui/tag/tag";
import { TypographyItem } from "../../../../../general-ui/typography-item/typography-item";
import { Button } from "../../../../../general-ui/button/button";
import Icon, { COLORS, ICONS } from "../../../../../general-ui/icon";
import EditableTextSlim from "../../../../../general-ui/editable-text/editable-text-slim";
import { getTextValue, updateTranslateKey } from "../../../../../../utils/utils";
import { UploadImageModal } from "../../../../../general-ui/image-upload-modal/image-upload-modal";
import { updateSessionInfo } from "../../../../../../store/actions/admin/create-event";

interface SessionCardProps {
	session: Session;
	stylingOverrides?: stylingOverrideReturns;
	template: TemplateClassNames;
	defaultImage: string;
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr;
	editDisabled?: boolean
}

const SessionCard: React.FC<SessionCardProps> = ({ session, language, baseLanguage, stylingOverrides = blankStyleOverrides, template, defaultImage, editDisabled = false }) => {
	const {
		CreateEventReducer: {
			editorSize
		},
		AuthReducer: {
			token,
		}
	} = useSelector((event: EventsState) => event);
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const dispatch = useDispatch();

	const { t } = useTranslation("homepage");

	const isLessThan768 = editorSize !== "desktop";

	const isBroadcast = session.session_type === SessionTypesEnum.broadcast;

	const {
		moduleCustomHeadingOverrides,
		secondaryButtonOverrides
	} = stylingOverrides;

	function handleTextChange(key: "description" | "title") {
		return (text: string) => {
			const _session: Session = {
				...session,
				[key]: updateTranslateKey({
					translateString: session[key] as TranslateString,
					input: text,
					language,
					baseLanguage
				})
			};
			dispatch(updateSessionInfo(_session, token!));
		};
	}

	const saveImage = useCallback((image: { original: string, edited: string; }) => {
		// TODO: update model to save orginal image file as well as edited
		const _session: Session = {
			...session,
			image: image.edited
		};
		dispatch(updateSessionInfo(_session, token!));
	}, [dispatch, session, token]);

	const { description, title } = getTextValue(session, language);
	return (
		<div className={classNames("agenda-session-card", { editDisabled })}>
			<div className={classNames("agenda-session-card-image", moduleCustomHeadingOverrides)}>
				{session.image ? <img src={session.image} alt={"session image"} /> : <img src={defaultImage} alt={"session image"} />}
				<div className="agenda-session-card-tags-wrapper">
					<Tag
						classNameTag={classNames(moduleCustomHeadingOverrides)}
						text={session?.session_type === "broadcast" ? t("Broadcast") : t("On Demand")}
						icon={"view" as TIconName}
					/>
					{/* live tag stub */}
					<Tag
						classNameTag={classNames("tag-container-live", moduleCustomHeadingOverrides)}
						text={t("live")}
					/>
				</div>

				<button className="round edit-image-button" onClick={() => setImageModalOpen(true)}>
					<Icon
						name={ICONS.EDIT}
						size={13}
						color={COLORS.WHITE}
					/>
				</button>
			</div>
			<div className="agenda-session-card-content">
				<div className="agenda-session-card-text-content">
					<TypographyItem
						className={classNames('agenda-session-card-timestamp', moduleCustomHeadingOverrides, {
							'evt-caption-1': template === TemplateClassNames.Apollo || template === TemplateClassNames["Studio 54"],
							'evt-caption-2 stable': template === TemplateClassNames.O2,
							// turn opacity to 0 and keep cursor default. This will keep the space (w/o content) where the date was
							isOnDemand: session.session_type === SessionTypesEnum.onDemand
						})}
					>
						{isBroadcast && new Date(session.timestamp!).toLocaleDateString()}
					</TypographyItem>
					<EditableTextSlim
						tagType="h3"
						className="agenda-session-card-title"
						onChange={handleTextChange("title")}
						value={title}
					/>

					<EditableTextSlim
						tagType="p"
						className="agenda-session-card-description"
						onChange={handleTextChange("description")}
						value={description}
					/>
				</div>
				<div className="agenda-session-card-buttons">
					<Button
						classButton={classNames(
							"primary",
							secondaryButtonOverrides)}
						template={template}
						onClick={() => null}
					>{t("Session details")}</Button>

					{isBroadcast
						? <Button
							onClick={() => null}
							classButton={classNames(
								"secondary",
								{ "with-svg": isLessThan768 },
								secondaryButtonOverrides)}
							template={template}
						>{isLessThan768
							? <Icon name={ICONS.CALENDAR_ADD} color="" size={22} />
							: t("Add to calendar")
							}</Button>
						: null
					}
				</div>
			</div>
			<UploadImageModal
				originalImage={session?.image}
				fileTitle={"Image"}
				open={imageModalOpen}
				close={() => setImageModalOpen(false)}
				onSave={saveImage}
			/>
		</div>
	);
};

export default SessionCard;